import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import './Gestiondesstocks.css';
import Card24 from '../components/Card24';
import Card25 from '../components/Card25';
import Card26 from '../components/Card26';
import Card27 from '../components/Card27';
import Card39 from '../components/Card39';
import Card43 from '../components/Card43';
import Camembert2 from '../components/Camembert2';
import Camembert4 from '../components/Camembert4';

/* ===================================================
   ★ CONFIGURATION
   =================================================== */
const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Cartes du haut (même ordre qu'avant) + titre du tableau ouvert
const CARTES_GS = [
  { key: 'card24', titre: 'Inventaires en cours',          Component: Card24 },
  { key: 'card25', titre: 'Inventaires à lancer',          Component: Card25 },
  { key: 'card26', titre: 'Historique des inventaires',    Component: Card26 },
  { key: 'card43', titre: 'Emplacements multi-références', Component: Card43 },
  { key: 'card27', titre: 'Emplacements vides',            Component: Card27 },
  { key: 'card39', titre: 'Tâches LOG',                    Component: Card39 },
];

// ★ Taille des camemberts (identique à la version d'origine)
const CAMEMBERT2 = { width: 500, height: 500 };
const CAMEMBERT4 = { width: 400, height: 400 };

const TABLE39_COLUMNS = ['Date', 'De', 'Description', 'Commentaires', 'Statut', 'Numero tache'];

const getTable39ColWidth = (col) =>
  col === 'Date'         ? '90px'  :
  col === 'De'           ? '90px'  :
  col === 'Description'  ? '480px' :
  col === 'Statut'       ? '120px' :
  col === 'Numero tache' ? '70px'  :
  col === 'Commentaires' ? '320px' : '150px';

const formatDate = (v) => {
  if (!v) return '';
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(String(v))) return v;
  const d = new Date(v);
  if (isNaN(d)) return v;
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const DATE_COLS_INVENTAIRE   = ['Date lancement inventaire', 'Date dernier inventaire'];
const HIDDEN_COLS_AVANCEMENT = ['ID', 'Client_id'];
const COLS_EDITABLES = ['1er comptage', '2ème comptage', '3ème comptage', 'resultat', 'causes', 'status'];

const estNombre = (v) => v !== null && v !== undefined && v !== '' && !isNaN(Number(String(v).replace(',', '.')));

// Statuts → couleur de pastille
const toneStatut = (val) => {
  const s = String(val || '').trim().toLowerCase();
  if (s === 'ouvert' || s === 'à faire') return 'warn';
  if (s === 'en cours') return 'info';
  if (s === 'soldé') return 'ok';
  return 'neutral';
};
const toneResultat = (val) => (val === 'ok' ? 'ok' : val === 'ko' ? 'danger' : 'neutral');

/* ===================================================
   TÂCHES — description découpée + commentaire auto-ajusté
   =================================================== */
const CLES_DESCRIPTION = [
  'Date de livraison prevue', 'Date de livraison prévue', 'Item', 'Commande client', 'Ligne',
  'Commande fournisseur', 'Nom fournisseur', 'Date de réception', 'Date de reception', 'Commentaire',
];
const echapper = (t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REGEX_CLES = new RegExp(
  `(${[...CLES_DESCRIPTION].sort((a, b) => b.length - a.length).map(echapper).join('|')})\\s*:`,
  'g'
);

const decouperDescription = (texte) => {
  if (texte == null) return null;
  const s = String(texte);
  const positions = [];
  let m;
  REGEX_CLES.lastIndex = 0;
  while ((m = REGEX_CLES.exec(s)) !== null) {
    positions.push({ label: m[1], debut: m.index, finLabel: REGEX_CLES.lastIndex });
  }
  if (positions.length < 2) return null;
  const champs = [];
  const avant = s.slice(0, positions[0].debut).trim();
  if (avant) champs.push({ label: 'Info', valeur: avant });
  positions.forEach((p, i) => {
    const fin = i + 1 < positions.length ? positions[i + 1].debut : s.length;
    const valeur = s.slice(p.finLabel, fin).trim();
    champs.push({ label: p.label.replace('prevue', 'prévue').replace('reception', 'réception'), valeur: valeur || '—' });
  });
  return champs;
};

const AutoTextarea = ({ value, ...props }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} value={value} rows={2} {...props} />;
};

const Pill = ({ tone, children }) => <span className={`gs-badge gs-badge-${tone}`}>{children}</span>;

// Message de retour (✅ / ⚠️ / ❌) en pastille
const Message = ({ texte }) => {
  if (!texte) return null;
  const tone = texte.startsWith('✅') ? 'ok' : texte.startsWith('⚠️') ? 'warn' : 'danger';
  return <span className={`gs-msg gs-msg-${tone}`}>{texte.replace(/^(✅|⚠️|❌)\s*/, '')}</span>;
};

/* ===================================================
   COMPOSANT
   =================================================== */
const Gestiondesstocks = () => {
  const [data, setData]                       = useState([]);
  const [avancementData, setAvancementData]   = useState([]);
  const [filteredData, setFilteredData]       = useState([]);
  const [emplacementData, setEmplacementData] = useState([]);
  const [tachesData, setTachesData]           = useState([]);
  const [nouvelleItem, setNouvelleItem]       = useState('');
  const [allItems, setAllItems]               = useState([]);
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  const [saveMsg, setSaveMsg]                 = useState('');
  const [inventaireSelection, setInventaireSelection] = useState({});
  const [saveLancementMsg, setSaveLancementMsg] = useState('');

  const [showDuplicatesOnly, setShowDuplicatesOnly]                         = useState(false);
  const [showDuplicatesDepotEmplacement, setShowDuplicatesDepotEmplacement] = useState(false);
  const [card24Clicked, setCard24Clicked] = useState(false);
  const [card25Clicked, setCard25Clicked] = useState(false);
  const [card26Clicked, setCard26Clicked] = useState(false);
  const [card27Clicked, setCard27Clicked] = useState(false);
  const [card39Clicked, setCard39Clicked] = useState(false);
  const [card43Clicked, setCard43Clicked] = useState(false);
  const [multiRefData, setMultiRefData]   = useState([]);
  const [searchItem, setSearchItem]       = useState('');
  const [isChartVisible, setIsChartVisible] = useState(true);

  useEffect(() => {
    document.title = 'MYSSA – Gestion des stocks';
  }, []);

  // ─────────────────────────────────────────────────────────────
  // Helpers doublons (Card43)
  // ─────────────────────────────────────────────────────────────
  const getDuplicateItems = (rows) => {
    const counts = {};
    rows.forEach(r => { if (r.Item) counts[r.Item] = (counts[r.Item] || 0) + 1; });
    return Object.keys(counts).filter(k => counts[k] > 1);
  };

  const getDuplicateDepotEmplacementPairs = (rows) => {
    const counts = {};
    rows.forEach(r => {
      const key = `${r['Nom Dépôt']}||${r['Emplacement']}`;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.keys(counts).filter(k => counts[k] > 1);
  };

  // ─────────────────────────────────────────────────────────────
  // Export Excel d'une ligne (Card24) — inchangé
  // ─────────────────────────────────────────────────────────────
  const exportRowToExcel = (row) => {
    const today = new Date();
    const dateStr = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
    const titre = `Inventaire de ${row['Item'] || ''} - ${row['Description'] || ''} ${dateStr}`;

    const cols = Object.keys(row).filter(c => !HIDDEN_COLS_AVANCEMENT.includes(c));

    const aoa = [
      [titre],
      [],
      cols,
      cols.map(c => {
        const v = row[c];
        if (v == null) return '';
        if (['date inventaire'].includes(c)) return formatDate(v);
        return v;
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: cols.length - 1 } }];
    ws['!cols'] = cols.map(() => ({ wch: 22 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventaire');
    XLSX.writeFile(wb, `Inventaire_${row['Item'] || 'item'}_${dateStr.replace(/\//g, '-')}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────
  // Chargement principal — inchangé
  // ─────────────────────────────────────────────────────────────
  const loadAvancement = () => {
    Promise.all([
      fetch(`${API}/avancement_inventaire`).then(r => r.json()),
      fetch(`${API}/inventaire_a_faire`).then(r => r.json()),
    ]).then(([avancement, inventaire]) => {

      setAvancementData(avancement.map(({ ID, Client_id, ...rest }) => rest));

      const actifs = avancement.filter(row => {
        const s = String(row['status'] || row['Status'] || '').trim().toLowerCase();
        return s === 'à faire' || s === 'en cours';
      });
      setFilteredData(actifs.map(row => ({
        ...row,
        '1er comptage':  row['1er comptage']  ?? '',
        '2ème comptage': row['2ème comptage'] ?? '',
        '3ème comptage': row['3ème comptage'] ?? '',
        causes:          row['causes']         ?? '',
        resultat:        row['resultat']        || 'ok',
        status:          row['status']          || 'à faire',
      })));

      const itemsActifs = new Set(
        avancement
          .filter(r => String(r['status'] || '').trim().toLowerCase() !== 'soldé')
          .map(r => String(r['Item'] || '').trim().toLowerCase())
      );
      const inventaireFiltre = inventaire.filter(r =>
        !itemsActifs.has(String(r['Item'] || '').trim().toLowerCase())
      );
      setData(inventaireFiltre.map(({ ID, Client_id, ...rest }) => rest));
      setInventaireSelection({});

    }).catch(e => console.error('Erreur chargement avancement/inventaire:', e));
  };

  useEffect(() => { loadAvancement(); }, []);

  useEffect(() => {
    fetch(`${API}/donnee_histogramme`)
      .then(r => r.json())
      .then(raw => {
        const items = [...new Set(raw.map(d => d['Item']).filter(Boolean))].sort();
        setAllItems(items);
      })
      .catch(e => console.error('Erreur items:', e));
  }, []);

  useEffect(() => {
    fetch(`${API}/emplacement_vide`)
      .then(r => r.json())
      .then(raw => setEmplacementData(raw.map(({ ID, Id, client_id, Client_id, ...rest }) => rest)))
      .catch(e => console.error('Erreur emplacement_vide:', e));
  }, []);

  const loadTachesLog = async () => {
    try {
      const res  = await fetch(`${API}/taches`);
      const rows = await res.json();
      const formatted = rows
        .map(({ ID, Client_id, Date: rawDate, ...rest }) => ({
          ...rest,
          Date: isNaN(new Date(rawDate)) ? '' : formatDate(new Date(rawDate)),
        }))
        .filter(r =>
          String(r['Département'] || r['Departement'] || '').toUpperCase() === 'LOG' &&
          ['ouvert', 'en cours'].includes(String(r['Statut'] || '').toLowerCase())
        )
        .sort((a, b) => new Date(a['Date'] || 0) - new Date(b['Date'] || 0));
      setTachesData(formatted);
    } catch (e) { console.error('Erreur tâches LOG:', e); }
  };

  const handleInputChange = (index, column, value) => {
    setFilteredData(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [column]: value };
      return next;
    });
  };

  const handleSaveAvancement = async (row) => {
    const payload = {
      ID:              row['ID'],
      Client_id:       row['Client_id'] || 'Client_A',
      '1er comptage':  row['1er comptage']  !== '' ? row['1er comptage']  : null,
      '2ème comptage': row['2ème comptage'] !== '' ? row['2ème comptage'] : null,
      '3ème comptage': row['3ème comptage'] !== '' ? row['3ème comptage'] : null,
      resultat:        row['resultat'] || null,
      causes:          row['causes']   || null,
      status:          row['status']   || 'à faire',
    };
    try {
      const res  = await fetch(`${API}/avancement_inventaire/update`, {
        method:  'PUT',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify(payload),
      });
      const json = await res.json();
      if (!json.success) console.error('Erreur sauvegarde :', json.error);
    } catch (e) { console.error('Erreur fetch handleSaveAvancement :', e); }
  };

  const handleEnregistrerInventaire = async () => {
    if (!nouvelleItem.trim()) {
      setSaveMsg('⚠️ Saisissez un item');
      setTimeout(() => setSaveMsg(''), 3000);
      return;
    }
    try {
      const res = await fetch(`${API}/avancement_inventaire`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ item: nouvelleItem.trim(), status: 'à faire' }),
      });
      const json = await res.json();
      if (json.success) { setSaveMsg('✅ Enregistré'); setNouvelleItem(''); loadAvancement(); }
      else setSaveMsg('❌ Erreur : ' + json.error);
    } catch (e) { setSaveMsg('❌ Erreur serveur'); console.error(e); }
    setTimeout(() => setSaveMsg(''), 4000);
  };

  const handleValiderLancement = async () => {
    const selection = data.filter((_, i) => inventaireSelection[i]);
    if (selection.length === 0) {
      setSaveLancementMsg('⚠️ Aucune ligne sélectionnée');
      setTimeout(() => setSaveLancementMsg(''), 3000);
      return;
    }
    let ok = 0, ko = 0;
    for (const row of selection) {
      try {
        const res  = await fetch(`${API}/avancement_inventaire`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ item: row['Item'], description: row['Description'] || '', stock: row['Stock actuel'] || null, status: 'à faire' }),
        });
        const json = await res.json();
        if (json.success) ok++; else ko++;
      } catch { ko++; }
    }
    setSaveLancementMsg(ko === 0 ? `✅ ${ok} inventaire(s) lancé(s)` : `⚠️ ${ok} OK / ${ko} erreur(s)`);
    setInventaireSelection({});
    loadAvancement();
    setTimeout(() => setSaveLancementMsg(''), 4000);
  };

  const handleCardClick = (card) => {
    setCard24Clicked(card === 'card24');
    setCard25Clicked(card === 'card25');
    setCard26Clicked(card === 'card26');
    setCard27Clicked(card === 'card27');
    setCard39Clicked(card === 'card39');
    setCard43Clicked(card === 'card43');
    setIsChartVisible(false);
    if (card === 'card24' || card === 'card25' || card === 'card26') loadAvancement();
    if (card === 'card39') loadTachesLog();
    if (card === 'card43') {
      fetch(`${API}/emplacement_multi_ref`)
        .then(r => r.json())
        .then(raw => setMultiRefData(raw.map(({ ID, Id, client_id, Client_id, ...rest }) => rest)))
        .catch(e => console.error('Erreur emplacement_multi_ref:', e));
    }
  };

  const retourGraphiques = () => {
    setCard24Clicked(false); setCard25Clicked(false); setCard26Clicked(false);
    setCard27Clicked(false); setCard39Clicked(false); setCard43Clicked(false);
    setIsChartVisible(true);
  };

  // ─────────────────────────────────────────────────────────────
  // Tâches LOG — mises à jour par n° de tâche
  // ─────────────────────────────────────────────────────────────
  const majTache = (numero, champ, valeur) => {
    setTachesData(prev => prev.map(r => (r['Numero tache'] === numero ? { ...r, [champ]: valeur } : r)));
  };

  const saveTacheCommentaire = async (row, commentaire) => {
    try {
      await fetch(`${API}/taches/update`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_tache: row['Numero tache'], commentaire: commentaire || '', statut: row['Statut'] || 'ouvert' }),
      });
      if (String(row['Numero tache']).startsWith('A')) {
        await fetch(`${API}/base_commentaire_animation/update`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ numero_tache: row['Numero tache'], commentaire: commentaire || '' }),
        });
      }
    } catch (e) { console.error('Erreur sauvegarde tâche LOG:', e); }
  };

  const handleTacheStatutChange = async (row, newStatut) => {
    majTache(row['Numero tache'], 'Statut', newStatut);
    try {
      await fetch(`${API}/taches/update`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_tache: row['Numero tache'], commentaire: row['Commentaires'] || '', statut: newStatut }),
      });
    } catch (e) { console.error('Erreur statut tâche LOG:', e); }
  };

  const avancementCols = filteredData.length > 0
    ? Object.keys(filteredData[0]).filter(c => !HIDDEN_COLS_AVANCEMENT.includes(c))
    : [];

  const inventaireColumns = data.length > 0 ? Object.keys(data[0]) : [];
  const nbSelection = Object.values(inventaireSelection).filter(Boolean).length;

  const carteActive =
    card24Clicked ? 'card24' : card25Clicked ? 'card25' : card26Clicked ? 'card26' :
    card43Clicked ? 'card43' : card27Clicked ? 'card27' : card39Clicked ? 'card39' : null;
  const titreActif = CARTES_GS.find(c => c.key === carteActive)?.titre;

  // ─────────────────────────────────────────────────────────────
  // Icône Excel
  // ─────────────────────────────────────────────────────────────
  const ExcelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <rect width="24" height="24" rx="5" fill="#1D6F42" />
      <path d="M13 3v5h5" stroke="#fff" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 12l2 3 2-3M9 18l2-3 2 3" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // Cellule générique (dates, nombres, Item…)
  const cellule = (col, value, key) => {
    if (DATE_COLS_INVENTAIRE.includes(col) || col === 'date inventaire') return <td key={key} className="gs-mono">{formatDate(value)}</td>;
    if (col === 'Item') return <td key={key} className="gs-mono gs-strong">{value ?? ''}</td>;
    if (col === 'Emplacement' || col === 'Nom Dépôt') return <td key={key} className="gs-mono">{value ?? ''}</td>;
    if (col === 'status' || col === 'Status') return <td key={key}><Pill tone={toneStatut(value)}>{value}</Pill></td>;
    if (col === 'resultat') return <td key={key}>{value ? <Pill tone={toneResultat(value)}>{value}</Pill> : ''}</td>;
    if (estNombre(value)) return <td key={key} className="num gs-mono">{value}</td>;
    return <td key={key}>{value ?? ''}</td>;
  };

  /* ===================================================
     RENDU
     =================================================== */
  return (
    <div className="gs-page">
      {/* ★ En-têtes et données centrés dans tous les tableaux (prioritaire sur Gestiondesstocks.css,
            y compris la classe "num" qui aligne les nombres à droite).
            Exceptions lisibilité : texte libre (causes, commentaires) et bloc multi-champs des tâches à gauche. */}
      <style>{`
        .gs-table th, .gs-table th.num,
        .gs-table td, .gs-table td.num { text-align: center !important; vertical-align: middle; }
        .gs-table .gs-cell-input { text-align: center; }
        .gs-table .gs-cell-input-wide,
        .gs-table .gs-textarea,
        .gs-table td.gs-td-desc { text-align: left !important; }
      `}</style>
      <div className="gs-container">

        {/* ── Header ── */}
        <div className="gs-header">
          <div className="gs-title-row">
            <div className="gs-accent" />
            <h1 className="gs-title">Gestion des stocks</h1>
            <span className="gs-chip-dept">LOG</span>
          </div>
      
        </div>

        {/* ── Cartes ── */}
        <div className="gs-cards" role="group" aria-label="Choisir un tableau">
          {CARTES_GS.map(({ key, titre, Component }) => {
            const actif = carteActive === key;
            return (
              <button
                key={key}
                type="button"
                className={`gs-card-btn ${actif ? 'active' : ''}`}
                aria-pressed={actif}
                aria-label={titre}
                onClick={() => handleCardClick(key)}
              >
                <Component />
              </button>
            );
          })}
        </div>

        {/* ── Graphiques ── */}
        {isChartVisible && (
          <div className="gs-graphs">
            <div className="gs-card gs-graph-card gs-graph-a">
              <Camembert2 width={CAMEMBERT2.width} height={CAMEMBERT2.height} />
            </div>
            <div className="gs-card gs-graph-card gs-graph-b">
              <Camembert4 width={CAMEMBERT4.width} height={CAMEMBERT4.height} />
            </div>
          </div>
        )}

        {/* ── Tableau de la carte ouverte ── */}
        {carteActive && (
          <div className="gs-card gs-table-card">
            <div className="gs-table-head">
              <div className="gs-table-head-left">
                <div className="gs-table-title">{titreActif}</div>
                {card24Clicked && <span className="gs-count">{filteredData.length}</span>}
                {card25Clicked && <span className="gs-count">{data.length}</span>}
                {card27Clicked && <span className="gs-count">{emplacementData.length}</span>}
                {card39Clicked && <span className="gs-count">{tachesData.length}</span>}
              </div>

              <div className="gs-table-head-right">
                {/* Card24 : ajouter une référence à inventorier */}
                {card24Clicked && (
                  <>
                    <div className="gs-search">
                      <svg className="gs-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                        <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
                      </svg>
                      <input
                        type="text"
                        className="gs-input"
                        placeholder="Entrer une référence"
                        aria-label="Référence à inventorier"
                        value={nouvelleItem}
                        onChange={e => { setNouvelleItem(e.target.value); setShowItemSuggestions(true); }}
                        onFocus={() => setShowItemSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowItemSuggestions(false), 150)}
                        onKeyDown={e => { if (e.key === 'Enter') handleEnregistrerInventaire(); }}
                        autoComplete="off"
                      />
                      {showItemSuggestions && nouvelleItem && (
                        <div className="gs-suggestions">
                          {allItems
                            .filter(r => r.toLowerCase().includes(nouvelleItem.toLowerCase()))
                            .slice(0, 30)
                            .map(ref => (
                              <button
                                type="button"
                                key={ref}
                                className="gs-suggestion"
                                onMouseDown={() => { setNouvelleItem(ref); setShowItemSuggestions(false); }}
                              >
                                {ref}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                    <button type="button" className="gs-btn gs-btn-primary" onClick={handleEnregistrerInventaire}>
                      + Enregistrer
                    </button>
                    <Message texte={saveMsg} />
                  </>
                )}

                {/* Card25 : valider le lancement */}
                {card25Clicked && (
                  <>
                    <Message texte={saveLancementMsg} />
                    <button type="button" className="gs-btn gs-btn-primary" onClick={handleValiderLancement} disabled={nbSelection === 0}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                      Valider lancement inventaire{nbSelection ? ` (${nbSelection})` : ''}
                    </button>
                  </>
                )}

                {/* Card26 : filtre par Item */}
                {card26Clicked && (
                  <div className="gs-search">
                    <svg className="gs-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                      <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
                    </svg>
                    <input
                      type="text"
                      className={`gs-input ${searchItem ? 'active' : ''}`}
                      placeholder="Filtrer par Item…"
                      aria-label="Filtrer par Item"
                      value={searchItem}
                      onChange={e => setSearchItem(e.target.value)}
                    />
                  </div>
                )}

                {/* Card43 : options d'affichage */}
                {card43Clicked && (
                  <div className="gs-toggles">
                    <label className={`gs-toggle ${showDuplicatesOnly ? 'active' : ''}`}>
                      <input type="checkbox" checked={showDuplicatesOnly} onChange={e => setShowDuplicatesOnly(e.target.checked)} />
                      Référence multi emplacement
                    </label>
                    <label className={`gs-toggle ${showDuplicatesDepotEmplacement ? 'active' : ''}`}>
                      <input type="checkbox" checked={showDuplicatesDepotEmplacement} onChange={e => setShowDuplicatesDepotEmplacement(e.target.checked)} />
                      Emplacement multi référence
                    </label>
                  </div>
                )}

                <button type="button" className="gs-btn gs-btn-ghost" onClick={retourGraphiques}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
                  Graphiques
                </button>
              </div>
            </div>

            <div className="gs-table-scroll">

              {/* ═══ CARD 24 — inventaires en cours (saisie des comptages) ═══ */}
              {card24Clicked && (
                filteredData.length === 0 ? (
                  <div className="gs-empty">Aucun inventaire en cours.</div>
                ) : (
                  <table className="gs-table">
                    <thead>
                      <tr>
                        {avancementCols.map(col => <th key={col}>{col}</th>)}
                        <th className="gs-th-icon" aria-label="Export Excel" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((row, index) => (
                        <tr key={index} className={`gs-row-${toneStatut(row['status'])}`}>
                          {avancementCols.map(col => {
                            if (!COLS_EDITABLES.includes(col)) return cellule(col, row[col], col);
                            if (col === 'resultat') {
                              return (
                                <td key={col}>
                                  <select
                                    className={`gs-select gs-select-${toneResultat(row['resultat'] || 'ok')}`}
                                    value={row['resultat'] || 'ok'}
                                    aria-label="Résultat"
                                    onChange={e => handleInputChange(index, 'resultat', e.target.value)}
                                    onBlur={() => handleSaveAvancement(filteredData[index])}
                                  >
                                    <option value="ok">ok</option>
                                    <option value="ko">ko</option>
                                  </select>
                                </td>
                              );
                            }
                            if (col === 'status') {
                              return (
                                <td key={col}>
                                  <select
                                    className={`gs-select gs-select-${toneStatut(row['status'])}`}
                                    value={row['status'] || 'à faire'}
                                    aria-label="Statut"
                                    onChange={e => handleInputChange(index, 'status', e.target.value)}
                                    onBlur={() => handleSaveAvancement(filteredData[index])}
                                  >
                                    <option value="à faire">à faire</option>
                                    <option value="en cours">en cours</option>
                                    <option value="soldé">soldé</option>
                                  </select>
                                </td>
                              );
                            }
                            return (
                              <td key={col}>
                                <input
                                  type="text"
                                  className={`gs-cell-input ${col === 'causes' ? 'gs-cell-input-wide' : ''}`}
                                  value={row[col] || ''}
                                  aria-label={col}
                                  onChange={e => handleInputChange(index, col, e.target.value)}
                                  onBlur={() => handleSaveAvancement(filteredData[index])}
                                />
                              </td>
                            );
                          })}
                          <td className="gs-td-icon">
                            <button type="button" className="gs-icon-btn" title="Exporter en Excel" aria-label="Exporter en Excel" onClick={e => { e.stopPropagation(); exportRowToExcel(row); }}>
                              <ExcelIcon />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* ═══ CARD 25 — inventaires à lancer (sélection) ═══ */}
              {card25Clicked && (
                data.length === 0 ? (
                  <div className="gs-empty">Aucun inventaire à lancer.</div>
                ) : (
                  <table className="gs-table">
                    <thead>
                      <tr>
                        <th className="gs-th-check">
                          <input
                            type="checkbox"
                            aria-label="Tout sélectionner"
                            checked={nbSelection > 0 && nbSelection === data.length}
                            onChange={e => {
                              const all = {};
                              if (e.target.checked) data.forEach((_, i) => { all[i] = true; });
                              setInventaireSelection(all);
                            }}
                          />
                        </th>
                        {inventaireColumns.map(col => <th key={col}>{col}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row, i) => (
                        <tr key={i} className={inventaireSelection[i] ? 'gs-row-selected' : ''}>
                          <td className="gs-td-check">
                            <input
                              type="checkbox"
                              aria-label={`Lancer l'inventaire de ${row['Item'] || ''}`}
                              checked={!!inventaireSelection[i]}
                              onChange={e => setInventaireSelection(prev => ({ ...prev, [i]: e.target.checked }))}
                            />
                          </td>
                          {inventaireColumns.map(col => cellule(col, row[col], col))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* ═══ CARD 26 — historique des inventaires ═══ */}
              {card26Clicked && (
                <table className="gs-table">
                  <thead>
                    <tr>{Object.keys(avancementData[0] || {}).map(col => <th key={col}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {avancementData
                      .filter(row => row.Item?.toLowerCase().includes(searchItem.toLowerCase()))
                      .map((row, i) => {
                        const s = String(row['status'] || row['Status'] || '').trim().toLowerCase();
                        const tone =
                          s === 'à faire' ? 'warn' :
                          s === 'en cours' ? 'info' :
                          row.resultat === 'ok' ? 'ok' :
                          row.resultat ? 'danger' : 'neutral';
                        return (
                          <tr key={i} className={`gs-row-${tone}`}>
                            {Object.keys(row).map(col => cellule(col, row[col], col))}
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}

              {/* ═══ CARD 43 — emplacements multi-références ═══ */}
              {card43Clicked && (
                <table className="gs-table">
                  <thead>
                    <tr>{Object.keys(multiRefData[0] || {}).map(col => <th key={col}>{col}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let lastPair = null;
                      let colorToggle = false;
                      return multiRefData
                        .filter(row => {
                          if (showDuplicatesDepotEmplacement) return getDuplicateDepotEmplacementPairs(multiRefData).includes(`${row['Nom Dépôt']}||${row['Emplacement']}`);
                          if (showDuplicatesOnly) return getDuplicateItems(multiRefData).includes(row.Item);
                          return true;
                        })
                        .sort((a, b) => {
                          if (!showDuplicatesDepotEmplacement) return 0;
                          if (a['Nom Dépôt'] < b['Nom Dépôt']) return -1;
                          if (a['Nom Dépôt'] > b['Nom Dépôt']) return 1;
                          if (a['Emplacement'] < b['Emplacement']) return -1;
                          if (a['Emplacement'] > b['Emplacement']) return 1;
                          return 0;
                        })
                        .map((row, i) => {
                          let groupe = '';
                          if (showDuplicatesDepotEmplacement) {
                            const currentPair = `${row['Nom Dépôt']}||${row['Emplacement']}`;
                            if (currentPair !== lastPair) { colorToggle = !colorToggle; lastPair = currentPair; }
                            groupe = colorToggle ? 'gs-group-a' : 'gs-group-b';
                          }
                          return (
                            <tr key={i} className={groupe}>
                              {Object.keys(row).map(col => cellule(col, row[col], col))}
                            </tr>
                          );
                        });
                    })()}
                  </tbody>
                </table>
              )}

              {/* ═══ CARD 27 — emplacements vides ═══ */}
              {card27Clicked && (
                <table className="gs-table">
                  <thead><tr>{Object.keys(emplacementData[0] || {}).map(col => <th key={col}>{col}</th>)}</tr></thead>
                  <tbody>
                    {emplacementData.map((row, i) => (
                      <tr key={i}>{Object.keys(row).map(col => cellule(col, row[col], col))}</tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* ═══ CARD 39 — tâches LOG ═══ */}
              {card39Clicked && (
                tachesData.length === 0 ? (
                  <div className="gs-empty">Aucune tâche LOG ouverte.</div>
                ) : (
                  <table className="gs-table gs-table-taches">
                    <thead>
                      <tr>{TABLE39_COLUMNS.map(col => <th key={col} style={{ minWidth: getTable39ColWidth(col) }}>{col}</th>)}</tr>
                    </thead>
                    <tbody>
                      {tachesData.map((row, rowIndex) => (
                        <tr key={row['Numero tache'] || rowIndex}>
                          {TABLE39_COLUMNS.map(col => {
                            if (col === 'Commentaires') return (
                              <td key={col} className="gs-td-comment">
                                <AutoTextarea
                                  className="gs-textarea"
                                  value={row['Commentaires'] || ''}
                                  placeholder="Ajouter un commentaire"
                                  aria-label={`Commentaire de la tâche ${row['Numero tache'] || ''}`}
                                  onChange={e => majTache(row['Numero tache'], 'Commentaires', e.target.value)}
                                  onBlur={e => saveTacheCommentaire(row, e.target.value)}
                                />
                              </td>
                            );
                            if (col === 'Statut') return (
                              <td key={col}>
                                <select
                                  className={`gs-select gs-select-${toneStatut(row['Statut'])}`}
                                  value={row['Statut'] || 'ouvert'}
                                  aria-label={`Statut de la tâche ${row['Numero tache'] || ''}`}
                                  onChange={e => handleTacheStatutChange(row, e.target.value)}
                                >
                                  <option value="ouvert">ouvert</option>
                                  <option value="en cours">en cours</option>
                                  <option value="soldé">soldé</option>
                                </select>
                              </td>
                            );
                            if (col === 'Description') {
                              const champs = decouperDescription(row['Description']);
                              if (champs) {
                                return (
                                  <td key={col} className="gs-td-desc">
                                    <div className="gs-desc-grid">
                                      {champs.map((c, idx) => {
                                        const vide = c.valeur === '—' || /^-+$/.test(c.valeur) || /pas de/i.test(c.valeur);
                                        return (
                                          <div key={idx} className={`gs-desc-field ${c.label === 'Commentaire' ? 'gs-desc-comment' : ''} ${vide ? 'gs-desc-empty' : ''}`}>
                                            <span className="gs-desc-label">{c.label}</span>
                                            <span className="gs-desc-value">{/^-+$/.test(c.valeur) ? '—' : c.valeur}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </td>
                                );
                              }
                              return (
                                <td key={col} className="gs-td-wrap">
                                  {row['Description'] && String(row['Description']).includes('Commentaire :') ? (
                                    String(row['Description']).split(/(Commentaire\s*:\s*[\s\S]*)/).map((part, idx) =>
                                      /Commentaire\s*:\s*[\s\S]+/.test(part)
                                        ? <span key={idx} className="gs-commentaire">{part}</span>
                                        : <span key={idx}>{part}</span>
                                    )
                                  ) : row['Description']}
                                </td>
                              );
                            }
                            if (col === 'Date') return <td key={col} className="gs-mono">{formatDate(row['Date'])}</td>;
                            if (col === 'Numero tache') return <td key={col} className="gs-mono">{row[col]}</td>;
                            return <td key={col}>{row[col] ?? ''}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Gestiondesstocks;